<template>
  <v-dialog
    v-model="isDialogOpen"
    :fullscreen="mobile"
    max-width="650"
    :persistent="canChangeSettings"
  >
    <template #activator="{ props }">
      <v-btn
        v-bind="props"
        variant="text"
        color="grey"
      >
        <v-icon
          :start="!mobile"
          icon="mdi-cog"
          size="large"
        />
        <span v-if="!mobile">{{ t('creator-ai-settings.title') }}</span>
      </v-btn>
    </template>

    <template #default>
      <v-card
        :class="{
          'pb-4': canChangeSettings,
          'creator-ai-settings__content--hidden': !canChangeSettings,
        }"
      >
        <v-card-title class="creator-ai-settings__header">
          <h1 class="text-h6">{{ $t('creator-ai-settings.title') }}</h1>
          <v-btn
            @click="isDialogOpen = false"
            icon="mdi-close"
            color="white"
            variant="text"
          />
        </v-card-title>
        <BlurredBackdropUpselling
          :is-blurred="!canChangeSettings"
          class="creator-ai-settings__upselling"
        >
          <div
            :class="{
              'creator-ai-settings__content': true,
              'creator-ai-settings__content--hidden': !canChangeSettings,
              'creator-ai-settings__content--padding-bottom': mobile,
            }"
          >
            <h2 class="text-subtitle-1">{{ $t('creator-ai-settings.subtitle') }}</h2>
            <v-form
              :key="`loading-${isActionPending}`"
              @submit.prevent="handleSubmit"
              ref="form"
            >
              <div
                v-for="field in contentFields"
                :key="field.label"
                class="creator-ai-settings__field"
              >
                <label class="creator-ai-settings__field-label">{{ field.label }}</label>
                <v-text-field
                  v-if="field.type === 'text'"
                  variant="outlined"
                  :loading="isActionPending"
                  v-model.trim="fields[field.modelValueKey]"
                  :rules="field?.rules"
                />
                <v-radio-group
                  v-if="field.type === 'radio'"
                  v-model="fields[field.modelValueKey]"
                  :rules="field?.rules"
                >
                  <v-radio
                    v-for="option in field.options"
                    :key="option.label"
                    :loading="isActionPending"
                    :label="option.label"
                    :value="option.value"
                    color="primary"
                  />
                </v-radio-group>
              </div>
              <div class="creator-ai-settings-actions">
                <v-btn
                  type="submit"
                  :loading="isActionPending"
                  >{{ $t('creator-ai-settings.save') }}</v-btn
                >
              </div>
            </v-form>
          </div>
        </BlurredBackdropUpselling>
      </v-card>
    </template>
  </v-dialog>
</template>
<script lang="ts" setup>
import { useLazyQuery, useMutation } from '@vue/apollo-composable'
import { computed, inject, ref, UnwrapRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import { VForm } from 'vuetify/components'

import BlurredBackdropUpselling from '@/components/BlurredBackdropUpselling.vue'
import { useBranding } from '@/composables/useBranding'
import { ProductType } from '@/lib/productTypes'
import { showSnackbarMessage } from '@/lib/snackbarMessages'

import GET_CREATOR_AI_SETTINGS from './queries/getCreatorAiSettings.gql'
import UPDATE_AI_SETTINGS from './queries/updateAiSettings.gql'

const $auth = inject('auth')

const { mobile } = useDisplay()
const { t } = useI18n()

const { load, result, loading } = useLazyQuery(GET_CREATOR_AI_SETTINGS)
const { mutate, loading: updateLoading } = useMutation(UPDATE_AI_SETTINGS, {
  update: (cache, { data }) => {
    cache.writeQuery({
      query: GET_CREATOR_AI_SETTINGS,
      data: {
        aiSettings: data.updateAiSettings,
      },
    })
  },
})

const form = ref<InstanceType<typeof VForm>>()

const { isPlatformMarkero } = useBranding()

const isDialogOpen = ref(false)
const fields = ref({
  targetGroup: '',
  formality: '',
  perspective: '',
  tonality: '',
  useHashtags: '',
})

type BaseField = {
  label: string
  modelValueKey: keyof UnwrapRef<typeof fields>
  rules?: Array<(value: string) => boolean>
}

type TextField = BaseField & {
  type: 'text'
}

type RadioField = BaseField & {
  type: 'radio'
  options: {
    label: string
    value:
      | 'formal'
      | 'informal'
      | 'singular'
      | 'plural'
      | 'professional'
      | 'casual'
      | 'medium'
      | 'none'
      | boolean
  }[]
}

type Field = RadioField | TextField

// @ts-expect-error:
const contentFields = computed<Field[]>(() =>
  [
    isPlatformMarkero.value && {
      type: 'text',
      label: t('creator-ai-settings.target-audience'),
      modelValueKey: 'targetGroup',
    },
    {
      type: 'radio',
      label: t('creator-ai-settings.sentence-structure'),
      modelValueKey: 'formality',
      options: [
        { label: t('creator-ai-settings.options.formal'), value: 'formal' },
        { label: t('creator-ai-settings.options.tonal'), value: 'informal' },
      ],
    },
    {
      type: 'radio',
      label: t('creator-ai-settings.perspective'),
      modelValueKey: 'perspective',
      options: [
        { label: t('creator-ai-settings.options.singular'), value: 'singular' },
        { label: t('creator-ai-settings.options.plural'), value: 'plural' },
      ],
    },
    {
      type: 'radio',
      label: t('creator-ai-settings.tonality'),
      modelValueKey: 'tonality',
      options: [
        { label: t('creator-ai-settings.options.professional'), value: 'professional' },
        { label: t('creator-ai-settings.options.casual'), value: 'casual' },
      ],
    },
    {
      type: 'radio',
      label: t('creator-ai-settings.hashtags'),
      modelValueKey: 'useHashtags',
      options: [
        { label: t('creator-ai-settings.options.include'), value: 'medium' },
        {
          label: t('creator-ai-settings.options.hashtags-none'),
          value: 'none',
        },
      ],
    },
  ].filter(Boolean)
)

const isActionPending = computed(() => loading.value || updateLoading.value)

const canChangeSettings = computed(() => {
  // eslint-disable-next-line no-unsafe-optional-chaining
  const { productType } = ($auth as any)?.user
  if (!productType) return false
  const notSupportedAccountTypes = [
    ProductType.BEGINNER,
    ProductType.LIGHT,
    ProductType.BASIC,
    ProductType.BEGINNER_TRIAL,
  ]
  if (notSupportedAccountTypes.includes(productType)) return false
  return true
})

const handleSubmit = async () => {
  const payload = {} as any
  const nestedKeys = ['useHashtags']
  for (const field of contentFields.value) {
    if (nestedKeys.includes(field.modelValueKey)) {
      if (!payload.contentCreatorSettings) payload.contentCreatorSettings = {}
      payload.contentCreatorSettings[field.modelValueKey] = fields.value[field.modelValueKey]
    } else {
      payload[field.modelValueKey] = fields.value[field.modelValueKey]
    }
  }

  await mutate({ input: payload })
  showSnackbarMessage('success', t('alerts.content-creator-ai-settings.success'))
  isDialogOpen.value = false
}

const setInitialValues = () => {
  if (!result.value) return

  const { aiSettings } = result.value

  fields.value.targetGroup = aiSettings?.targetGroup
  fields.value.formality = aiSettings?.formality
  fields.value.perspective = aiSettings?.perspective
  fields.value.tonality = aiSettings?.tonality
  fields.value.useHashtags = aiSettings?.contentCreatorSettings?.useHashtags ?? ''
}

watch(isDialogOpen, async (value: boolean) => {
  if (value) {
    await load()
    setInitialValues()
  }
})

watch(result, setInitialValues)
</script>
<style scoped>
.creator-ai-settings__header {
  display: flex;
  position: sticky;
  top: 0;
  z-index: 10;
  justify-content: space-between;
  align-items: center;
  background-color: rgb(var(--v-theme-primary));
  color: white;
}
.creator-ai-settings__content {
  padding: 8px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.creator-ai-settings__content--padding-bottom {
  padding-bottom: 4rem;
}
.creator-ai-settings__content--hidden {
  overflow-y: hidden !important;
  height: 100%;
}
.creator-ai-settings__field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.creator-ai-settings__field-label {
  font-weight: 600;
}
.creator-ai-settings-actions {
  display: flex;
  padding-bottom: 16px;
  justify-content: end;
}
:deep(.creator-ai-settings__upselling .centered-alert) {
  margin: 0 16px;
}
</style>
